import React, { useEffect, useMemo } from "react";
import { JournalEntrySourceFilter as JournalEntrySourceFilterValue } from "@common/schemas/domains/accounting/journalEntry";
import { journalEntrySourceFilterToLabelMap } from "@components/accounting/constants";
import { useIntegrationConnections } from "@api/integrationConnection.api";
import { useWorkspaceAccountingMigrations } from "@api/accountingIntegration.api";
import { ConnectionProvider } from "@common/types/integrationConnection";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useScope } from "@state/scopeContext.lib";
import { BaseFilterProps } from "../../lib";
import { getHiddenJournalEntrySourceFilters } from "./journalEntrySourceFilter.lib";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

const allOptions: FilterMultiselectOption[] = Object.values(
    JournalEntrySourceFilterValue,
).map((value) => ({
    value,
    label: journalEntrySourceFilterToLabelMap[value],
    searchableLabel: journalEntrySourceFilterToLabelMap[value],
}));

interface Props extends BaseFilterProps {
    sourceOptions?: JournalEntrySourceFilterValue[];
}

export const JournalEntrySourceFilter: React.FC<Props> = ({
    filters,
    onChange,
    sourceOptions,
}) => {
    const { workspaceId } = useScope();
    const { data: quickbooksConnections, isSuccess: isConnectionsLoaded } =
        useIntegrationConnections({
            provider: ConnectionProvider.QUICKBOOKS,
        });
    const { data: accountingMigrations, isSuccess: isMigrationsLoaded } =
        useWorkspaceAccountingMigrations(workspaceId ?? null);

    const hiddenFilters = useMemo(
        () =>
            getHiddenJournalEntrySourceFilters({
                hasQuickBooksConnection:
                    (quickbooksConnections?.length ?? 0) > 0,
                migrations: accountingMigrations ?? [],
            }),
        [quickbooksConnections, accountingMigrations],
    );

    const options = useMemo(
        () =>
            allOptions.filter(
                (option) =>
                    (!sourceOptions ||
                        sourceOptions.some(
                            (sourceOption) => sourceOption === option.value,
                        )) &&
                    !hiddenFilters.some(
                        (hiddenFilter) => hiddenFilter === option.value,
                    ),
            ),
        [hiddenFilters, sourceOptions],
    );

    useEffect(() => {
        if (!isConnectionsLoaded || !isMigrationsLoaded) {
            return;
        }

        const currentSources = filters.journalEntrySources ?? [];
        const newSources = currentSources.filter(
            (source) =>
                !hiddenFilters.some((hiddenFilter) => hiddenFilter === source),
        );

        if (newSources.length !== currentSources.length) {
            onChange({
                journalEntrySources:
                    newSources.length > 0 ? newSources : undefined,
            });
        }
    }, [
        filters.journalEntrySources,
        hiddenFilters,
        onChange,
        isConnectionsLoaded,
        isMigrationsLoaded,
    ]);

    const journalEntrySources = filters.journalEntrySources ?? [];

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                searchable={false}
                options={options}
                value={journalEntrySources}
                onChange={(val) => {
                    onChange({
                        journalEntrySources:
                            val.length > 0
                                ? (val as JournalEntrySourceFilterValue[])
                                : undefined,
                    });
                }}
                name="journalEntrySources"
                onBlur={noop}
            />
        </div>
    );
};
